import { ArrowLeft } from 'lucide-react';
import { useBusinessProfile } from '../hooks/useBusinessProfile.js';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { storageService } from '../domain/services/StorageService.js';
import {
  LoadingScreen, ErrorScreen, HeroSection, AboutSection, GeneralInfoCard,
  PhotoGalleryCard, LocationAccessCard, EmergencyContactsCard, OpeningHoursCard,
  SocialLinksCard, RelatedBusinesses, GalleryModal, MapModal, QRPlateModal, ImagePreviewModal
} from '../components/profile/SharedProfileComponents.jsx';
import MealScheduleCard from '../components/profile/MealScheduleCard.jsx';
import WeatherCard from '../components/profile/WeatherCard.jsx';
import CountsCard from '../components/profile/CountsCard.jsx';
import AssetsSection from '../components/entity-profile/AssetsSection.jsx';
import BannersSection from '../components/entity-profile/BannersSection.jsx';
import RatingsSection from '../components/entity-profile/RatingsSection.jsx';

const MyBusinessProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const vm = useBusinessProfile(id, 'id', true);

  // Authentication check - redirect to login if not authenticated
  useEffect(() => {
    if (!storageService.hasAuthToken()) {
      console.log('No auth token found, redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Ownership check - redirect to entity profile if user doesn't own this business
  useEffect(() => {
    if (!vm.loading && vm.business && !vm.isOwner && storageService.hasAuthToken()) {
      console.log('User does not own this business, redirecting to entity profile');
      // Redirect to public entity profile instead
      navigate(`/entity/${vm.business.tag || vm.business.id}`, { replace: true });
    }
  }, [vm.loading, vm.business, vm.isOwner, navigate]);

  // If not authenticated, show loading while redirecting
  if (!storageService.hasAuthToken()) {
    return <LoadingScreen />;
  }

  if (vm.loading) return <LoadingScreen />;
  if (vm.error || !vm.business) return <ErrorScreen error={vm.error} navigate={vm.handleBack} />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={vm.handleBack}
          className="flex items-center gap-2 text-green-400 hover:text-green-300 mb-6 transition-all duration-300 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Back to My Businesses
        </button>

        <HeroSection vm={vm} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="lg:col-span-2 space-y-6">
            <CountsCard counts={vm.counts} />
            <AboutSection vm={vm} />
            <GeneralInfoCard business={vm.business} />
            {vm.banners.length > 0 && <BannersSection banners={vm.banners} />}
            {/* {vm.assets.length > 0 && <AssetsSection assets={vm.assets} />} */}
            <PhotoGalleryCard vm={vm} />
            <OpeningHoursCard business={vm.business} workingHours={vm.workingHours} isCurrentlyOpen={vm.isCurrentlyOpen} convertTo12Hour={vm.convertTo12Hour} />
            <MealScheduleCard business={vm.business} />
          </div>

          <div className="space-y-6">
            <LocationAccessCard business={vm.business} onShowMap={() => vm.setShowMapModal(true)} />
            <WeatherCard weather={vm.weather} />
            <EmergencyContactsCard category={vm.business.category?.name} />
            {vm.ratings.length > 0 && <RatingsSection ratings={vm.ratings} />}
            <SocialLinksCard socialLinks={vm.socialLinks} />
          </div>
        </div>

        {/* Related businesses section removed */}
      </div>

      <GalleryModal vm={vm} />
      <MapModal vm={vm} />
      <QRPlateModal vm={vm} />
      <ImagePreviewModal showImagePreview={vm.showImagePreview} setShowImagePreview={vm.setShowImagePreview} previewImage={vm.previewImage} />
    </div>
  );
};

export default MyBusinessProfile;
